import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { CountrySelectionDropdown } from '../../shared-components/country-selection-dropdown/CountrySelectionDropdown';
import { FlexBox } from '../../shared-components/flex-box';
import { FormattedInput } from '../../shared-components/form';
import { PartnerTypeSelectionDropdown } from '../../shared-components/partner-type-selection-dropdown/PartnerTypeSelectionDropdown';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import { CompanyInformation } from './CompanyInformation';
import './CompanyInformationSection.scss';
import { CountryEligibilityModal } from './CountryEligibilityModal';

interface CompanyInformationSectionProps {
  onGetCompanyInformation: (accountId: string) => Promise<void>;
  onSelectMpnIdInputOption: (showMpnIdSelection: boolean) => void;
}

const CompanyInformationSectionImpl: React.FC<CompanyInformationSectionProps> = (props) => {
  const {
    onGetCompanyInformation,
    onSelectMpnIdInputOption,
  } = props;

  const {
    partnerTypeStore,
    userStore,
    nominationWizardPageUiStore,
  } = useStores();

  const isBeingValidated = nominationWizardPageUiStore.isBeingValidated('company-information');

  const commonStyles = useCommonStyles();

  const [showCountryEligibilityModal, setShowCountryEligibilityModal] = useState<boolean>(false);

  const validationErrors = nominationWizardPageUiStore.getCompanyInformationValidation(
    partnerTypeStore.otherPartnerTypeId,
  ).fieldErrors;

  return (
    <FlexBox
      aligns='start'
      direction='column'
      className='company-information-section'
    >
      <CompanyInformation
        accounts={userStore.userMpnIds}
        organization={nominationWizardPageUiStore.nominationOrganization}
        displayMpnIdSelectionPreferance
        validationErrors={validationErrors}
        displayRefreshFromPartnerCenter
        onGetCompanyInformation={onGetCompanyInformation}
        displayOrganizationContactForm={false}
        onSelectMpnIdInputOption={onSelectMpnIdInputOption}
      />
      {
          nominationWizardPageUiStore.isCountryAward && (
              <>
                  <CountrySelectionDropdown
                      id='associated-country'
                      associatedCountries={nominationWizardPageUiStore.editingAssociatedCountries}
                      required
                      label='Country associated with nomination'
                      hint='By selecting a country, you will be automatically included in the nomination pool for the country’s Partner of the Year Award.'
                      validationMessage={
                        isBeingValidated
                        && validationErrors.editingAssociatedCountries?.length
                          ? (
                            <ValidationMessageList
                              messages={validationErrors.editingAssociatedCountries}
                            />
                          )
                          : undefined
                      }
                  />
                  {/*<Link
                      as='button'
                      className={commonStyles.buttonAsLink}
                      onClick={() => setShowCountryEligibilityModal(true)}
                      >
                      View Country Partner of the Year eligibility criteria here
                      </Link>*/}
              </>
          )
      }
      <PartnerTypeSelectionDropdown
        partnerTypeOptions={partnerTypeStore.partnerTypeOptions}
        nominationPartnerTypes={nominationWizardPageUiStore.nomination.partnerTypes}
        required
        validationMessage={
          isBeingValidated
          && validationErrors.partnerTypes?.length
            ? (
              <ValidationMessageList
                messages={validationErrors.partnerTypes}
              />
            )
            : undefined
        }
        label={{
          children: 'Which partner type(s) best describe your company?',
          className: commonStyles.inputFieldLabel,
        }}
        otherField={nominationWizardPageUiStore.nomination.partnerTypes.some((el) => partnerTypeStore.isOtherPartnerType(el.partnerTypeId)) ? {
          onGetOtherValue: (value) => nominationWizardPageUiStore.setOtherPartnerTypeDescription(value, partnerTypeStore.otherPartnerTypeId),
          validationMessageForOther: isBeingValidated
          && validationErrors.partnerTypes?.length
            ? (
              <ValidationMessageList
                messages={validationErrors.partnerTypes}
              />
            )
            : undefined,
        } : undefined}
      />
      <FormattedInput
        prefix={'https://'}
        label='Marketplace Offer'
        value={nominationWizardPageUiStore.nomination.marketplaceOfferUrl ?? ''}
        onChange={marketplaceOfferUrl => nominationWizardPageUiStore.setMarketplaceOfferUrl(marketplaceOfferUrl)}
        errorText={
          isBeingValidated
          && validationErrors.marketplaceOfferUrl?.length
            ? (
              <ValidationMessageList
                messages={validationErrors.marketplaceOfferUrl}
              />
            )
            : undefined
        }
        maxLength={512}
        isValidated={isBeingValidated}
      />
      {
        showCountryEligibilityModal && (
          <CountryEligibilityModal
            onCloseModal={() => setShowCountryEligibilityModal(false)}
          />
        )
      }
    </FlexBox>
  );
}

export const CompanyInformationSection = observer(CompanyInformationSectionImpl);